<script>
import {getCurrentInstance, onMounted, onBeforeUnmount, computed, ref} from 'vue';
import commonFn from '@/commons/commonFunction';
import focusUtility from '@/commons/focusUtility';
import MsCheckbox from '../checkbox/MsCheckbox.vue';
import {shortkeyStatusbar} from '@/mixins/common/shortkeyStatusbar';
import {MSConstant} from '@/commons/wildcards';

export default {
  mixins: [shortkeyStatusbar],
  components: {MsCheckbox},
  props: {
    title: String,
    width: {
      type: Number,
      default: 400,
    },
    message: [String, Array],
    type: {
      type: Number,
      default: 0,
    },
    hideClose: {
      type: Boolean,
      default: false,
    },
    textBtn: {
      type: String,
      default: null,
    },
    textBtnClose: {
      type: String,
      default: null,
    },
    textBtnCustom: {
      type: String,
      default: null,
    },
    listBtn: {
      type: Array,
      default: [],
    },
    /**
     * Thêm optionList cho message có dạng chọn
     * tbngoc 22.07.2022
     */
    optionList: {
      type: Array,
      default: null,
    },
    customLabelIsShowMessage: {
      type: String,
      default: null,
    },
    /**
     * Object custom text của button VD: showConfirmValidate(message, title, width, customTextButton)
     */
    customTextButton: {
      type: Object,
      default: {Yes: 'Có', No: 'Không'},
    },
    /**
     * custom focus button khi mở message box
     * dvhung2 25.02.2025
     */
    customFocusBtn: {
      type: Function,
      default: null,
    },
  },

  setup(props) {
    /**
     * selector các control focus được trong message box
     */
    const primaryButtonSelector = 'button.primary';
    const focusableElements = ref([]);
    const {proxy} = getCurrentInstance();
    const root = ref(null);
    /**
     * Tìm element button primary trong message box
     */
    const findPrimaryButton = () => {
      if (!proxy?.$refs?.root) return null;
      if (props.customFocusBtn && typeof props.customFocusBtn === 'function') {
        return props.customFocusBtn(proxy.$refs.root);
      }
      return proxy.$refs.root.querySelector(primaryButtonSelector);
    };

    const initTrapFocus = () => {
      focusableElements.value = focusUtility.getFocusableElements(proxy.$refs.root);
    };

    const focusDefault = () => {
      const primaryButton = findPrimaryButton();
      if (primaryButton) {
        primaryButton.focus();
      } else {
        focusableElements.value[0].focus();
      }
    };

    const handleTab = event => {
      focusUtility.handleTab(event, focusableElements);
    };

    /**
     * Action luôn ở đây ko theo base chung do chỉ dùng esc
     */
    const shortkeyAction = e => {
      if (e.originEvent.cancel) {
        return;
      }
      const me = proxy;
      const shortkey = e.target.getAttribute('shortkey');
      if (shortkey && shortkey.split('|').indexOf(e.srcKey) > -1) {
        if (Array.isArray(e.commandStopNativeEvent) && e.commandStopNativeEvent.some(x => x === e.srcKey)) {
          e.originEvent.preventDefault();
          e.originEvent.stopPropagation();
          e.originEvent.cancel = true;
        }

        // click vào body để unfocus khỏi control hiện tại -> submit giá trị đang nhập
        document.body.click();

        const selector = ['[shortkey-target="', e.srcKey, '"]'].join('');
        const target = root.value.querySelector(selector);

        if (target) {
          if (target.classList.contains('split-button')) {
            const button = target.querySelector('button');
            if (button) {
              button.click();
            }
          } else {
            target.click();
          }
        } else {
          setTimeout(() => {
            // gọi hàm xử lý tương ứng với phím tắt
            me.buttonClick(e.srcKey, e);
          }, 10);
        }
      }
    };

    const shortkey = {
      Close: ['esc'], // Đóng
      Quit: ['ctrl', 'q'], // Thoát
    };

    const defaultShortKey = computed(() => {
      return shortkey;
    });

    const IsShowMessage = ref(true); // Dùng cho việc ẩn hiện thông báo sinh chứng từ
    const option = ref(1); // Option trả về
    onMounted(() => {
      // focus button
      initTrapFocus();
      focusDefault();
      commonFn.shortkeyPushView(root.value);
    });

    /**
     * Loại bỏ el dùng cho shortkey
     */
    onBeforeUnmount(() => {
      commonFn.shortkeyPopView(root.value);
      focusableElements.value = [];
    });

    function escPress(e, close) {
      e.preventDefault();
      e.stopPropagation();
      close(this, null);
    }

    return {
      // returnValue,
      escPress,
      IsShowMessage,
      shortkeyAction,
      defaultShortKey,
      option,
      root,
      handleTab,
      MSConstant
    };
  },
};
</script>

<template>
  <!-- Đợi thiết kế chỉnh lại style sau -->
  <div class="msg-box" ref="root" @keydown.tab="handleTab">
    <span v-shortkey="defaultShortKey" class="msg-box-shortkey" @shortkey="shortkeyAction" />
    <div ref="msg" class="msg-center" :style="{width: width + 'px'}">
      <div class="msg-title">{{ title }}</div>
      <button
        v-if="!hideClose"
        class="btn icon-button only-icon msg-close icon24 close"
        @click="$close(this, null)"
        v-tooltip="MSConstant.TooltipShortkey.Esc"
        shortkey-target="Close"
      ></button>
      <div class="msg-message">
        <template v-if="Array.isArray(message)">
          <div v-for="(item, i) in message" :key="i" class="msg-item" v-safe-html="item"></div>
        </template>
        <div v-else class="msg-item" v-safe-html="message"></div>

        <!-- confirm không hiện lại popup này lần sau -->
        <div v-if="type == 7" class="mt-1">
          <ms-checkbox
            :label="customLabelIsShowMessage || 'Không hiển thị lần sau'"
            @change="IsShowMessage = !IsShowMessage"
          ></ms-checkbox>
        </div>

        <div v-if="type == 9" class="flex mt-1">
          <ms-radio
            v-for="(item, index) in optionList"
            :key="index"
            v-model="option"
            :label="item.text"
            name="rdnameMessageBox"
            :key-value="item.value"
            class="mb-2"
          />
          <div class="hr-row"></div>
          <ms-checkbox
            :label="customLabelIsShowMessage || 'Không hiển thị lần sau'"
            @change="IsShowMessage = !IsShowMessage"
          ></ms-checkbox>
        </div>
        <div v-if="type == 20" class="flex mt-1">
          <ms-radio
            v-for="(item, index) in optionList"
            :key="index"
            v-model="option"
            :label="item.text"
            name="rdnameMessageBoxCustom"
            :key-value="item.value"
            class="mb-2"
          />
        </div>
      </div>

      <!-- alert: info/warning -->
      <div v-if="type == $ms.constant.MessageBoxType.confirm_Info_Warning" class="msg-buttons flex-center">
        <button class="msg-btn btn primary" @click="$close(this, null)">
          {{ $t('i18nComponent.MessageBox.Accept') }}
        </button>
      </div>
      <!-- confirm yes_no-->
      <div v-else-if="type == $ms.constant.MessageBoxType.confirm_Yes_No" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)" >
          {{ $t('i18nComponent.MessageBox.No') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, true)">
          {{ $t('i18nComponent.MessageBox.Yes') }}
        </button>
      </div>

      <!-- confirm lấy số bội chi-->
      <div v-else-if="type == 111" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ $t('i18nComponent.MessageBox.Close') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, true)">
          {{ $t('i18nComponent.MessageBox.Estimate') }}
        </button>
      </div>

      <!-- confirm yes_no_cancel-->
      <div v-else-if="type == $ms.constant.MessageBoxType.confirm_Yes_No_Cancel" class="msg-buttons flex-row">
        <button class="msg-btn btn secondary-outline" @click="$close(this, null)">
          {{ $t('i18nComponent.MessageBox.Cancel') }}
        </button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ $t('i18nComponent.MessageBox.No') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, true)">
          {{ $t('i18nComponent.MessageBox.Yes') }}
        </button>
      </div>
      <!-- confirm delete-->
      <div v-else-if="type == $ms.constant.MessageBoxType.confirm_Delete" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ $t('i18nComponent.MessageBox.No') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, true)">
          {{ $t('i18nComponent.MessageBox.Yes') }}
        </button>
      </div>
      <!-- confirm Đóng -->
      <div v-else-if="type == $ms.constant.MessageBoxType.confirm_Close" class="msg-buttons flex-center">
        <button class="msg-btn btn primary" @click="$close(this, null)" 
          v-tooltip="MSConstant.TooltipShortkey.Quit"
          shortkey-target="Quit">
          {{ $t('i18nComponent.MessageBox.Close') }}
        </button>
      </div>
      <!-- confirm yes_no-->
      <div v-else-if="type == 5 || type == 7" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button
          class="msg-btn btn secondary-outline"
          @click="
            $close(
              this,
              type == 7
                ? {
                    answer: false,
                    IsShowMessage: IsShowMessage,
                  }
                : false,
            )
          "
        >
          {{ customTextButton.No }}
        </button>
        <button
          class="msg-btn btn primary"
          @click="
            $close(
              this,
              type == 7
                ? {
                    answer: true,
                    IsShowMessage: IsShowMessage,
                  }
                : true,
            )
          "
        >
          {{ customTextButton.Yes }}
        </button>
      </div>

      <div v-if="type == 6" class="msg-buttons flex-center">
        <button
          class="msg-btn btn primary"
          @click="$close(this, true)"
          v-tooltip="MSConstant.TooltipShortkey.Quit"
          shortkey-target="Quit"
        >
          {{ textBtn }}
        </button>
      </div>

      <div v-else-if="type == 8" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ $t('i18nComponent.MessageBox.Close') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, true)">Xem phát sinh</button>
      </div>

      <div v-else-if="type == 9" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button
          class="msg-btn btn secondary-outline"
          @click="
            $close(this, {
              answer: false,
              IsShowMessage: IsShowMessage,
              option,
            })
          "
        >
          {{ $t('i18nComponent.MessageBox.No') }}
        </button>
        <button
          class="msg-btn btn primary"
          @click="
            $close(this, {
              answer: true,
              IsShowMessage: IsShowMessage,
              option,
            })
          "
        >
          {{ $t('i18nComponent.MessageBox.Yes') }}
        </button>
      </div>

      <div v-else-if="type == 10" style="gap: 10px" class="msg-buttons flex-row revert-button">
        <button class="msg-btn btn primary" @click="$close(this, $ms.constant.ModelState.Update)">
          {{ $t('i18nComponent.MessageBox.Update') }}
        </button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, $ms.constant.ModelState.Insert)">
          {{ $t('i18nComponent.MessageBox.Add') }}
        </button>
        <div class="flex" />
      </div>

      <div v-else-if="type == 11" style="gap: 10px" class="msg-buttons flex-row revert-button">
        <button class="msg-btn btn primary" @click="$close(this, true)">
          {{ $t('i18nComponent.MessageBox.Update') }}
        </button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ $t('i18nComponent.MessageBox.Close') }}
        </button>
        <div class="flex" />
      </div>
      <!-- cảnh báo 2 button dvhung2 17.04.2024 -->
      <div v-else-if="type == 12" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ $t('i18nComponent.MessageBox.Close') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, true)">{{ textBtn }}</button>
      </div>
      <!-- cảnh báo 3 button dvhung2 11.06.2024 -->
      <div v-else-if="type == 13" class="msg-buttons flex-row">
        <div class="flex"></div>
        <button class="msg-btn btn secondary-outline" @click="$close(this, 1)">
          {{ $t('i18nComponent.MessageBox.Merge') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, 2)">
          {{ $t('i18nComponent.MessageBox.Delete') }}
        </button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, 3)">
          {{ $t('i18nComponent.MessageBox.Rename') }}
        </button>
      </div>
      <div v-else-if="type == 16" class="msg-buttons flex-center">
        <button class="msg-btn btn primary" @click="$close(this, true)">
          {{ $t('i18nComponent.MessageBox.Accept') }}
        </button>
      </div>
      <div v-else-if="type == 20" class="msg-buttons flex-center">
        <button class="msg-btn btn secondary-outline" @click="$close(this, {answer: false, option})">
          {{ $t('i18nComponent.MessageBox.Cancel') }}
        </button>
        <button class="msg-btn btn primary" @click="$close(this, {answer: true, option})">
          {{ $t('i18nComponent.MessageBox.Accept') }}
        </button>
      </div>
      <div v-else-if="type == 15" style="gap: 10px" class="msg-buttons flex-row revert-button">
        <button class="msg-btn btn primary" @click="$close(this, 2)">{{ textBtn }}</button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, 1)">
          {{ $t('i18nComponent.MessageBox.Cancel') }}
        </button>
        <div class="flex"></div>
      </div>
      <div v-else-if="type == 17" style="gap: 10px" class="msg-buttons flex-row revert-button">
        <button class="msg-btn btn primary" @click="$close(this, true)">{{ textBtn }}</button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ textBtnClose ?? $t('i18nComponent.MessageBox.Cancel') }}
        </button>
        <div class="flex"></div>
      </div>
      <!-- thêm popup custom cả 2 nút theo text truyền vào -->
      <div v-else-if="type == 21" style="gap: 10px" class="msg-buttons flex-row revert-button">
        <button class="msg-btn btn primary" @click="$close(this, true)">{{ textBtnCustom }}</button>
        <button class="msg-btn btn secondary-outline" @click="$close(this, false)">
          {{ textBtn }}
        </button>
        <div class="flex"></div>
      </div>

      <div v-else-if="type == 22" style="gap: 8px" class="msg-buttons flex-row revert-button">
        <button
          v-for="item in listBtn"
          :key="item.id"
          @click="$close(this, item.value)"
          class="btn"
          :class="item.primary ? 'primary' : 'secondary-outline'"
        >
          {{ item.text }}
        </button>

        <div class="flex"></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/components/MsMessageBox.scss';
.revert-button {
  flex-direction: row-reverse;
}
</style>

```